import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { DialogContentText } from "@mui/material";
import { DangerButton } from "@shared/CustomButton";
import { Modal } from "@shared/Modal/Modal";

type RunDiscard = (discard: () => void) => void;

type GuardContext = {
    confirmDiscard: RunDiscard;
    discard: RunDiscard;
    reportUnsavedChanges: (reporterId: string, hasUnsavedChanges: boolean) => void;
};

const UnsavedChangesGuardContext = createContext<GuardContext>({
    confirmDiscard: (discard) => discard(),
    discard: (discard) => discard(),
    reportUnsavedChanges: () => undefined,
});

type Props = {
    hasUnsavedChanges?: boolean;
};

export const UnsavedChangesGuard = ({
    hasUnsavedChanges: hasOwnUnsavedChanges = false,
    children,
}: React.PropsWithChildren<Props>) => {
    const { t } = useTranslation(["modals"]);
    const [pendingDiscard, setPendingDiscard] = useState<(() => void) | null>(null);
    const [reportersWithChanges, setReportersWithChanges] = useState<ReadonlySet<string>>(() => new Set());
    const isDiscarding = useRef(false);

    const hasUnsavedChanges = hasOwnUnsavedChanges || reportersWithChanges.size > 0;

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            !isDiscarding.current && hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    );

    const discard: RunDiscard = (run) => {
        // The blocker only sees this flag while run() is still on the stack, so run must navigate synchronously:
        // a navigation after an await is blocked again and the user is asked a second time.
        isDiscarding.current = true;
        try {
            run();
        } finally {
            isDiscarding.current = false;
        }
    };

    const confirmDiscard: RunDiscard = (run) => {
        if (hasUnsavedChanges) {
            setPendingDiscard(() => run);
        } else {
            run();
        }
    };

    const reportUnsavedChanges = React.useCallback((reporterId: string, reporterHasChanges: boolean) => {
        setReportersWithChanges((current) => {
            if (current.has(reporterId) === reporterHasChanges) {
                return current;
            }
            const next = new Set(current);
            if (reporterHasChanges) {
                next.add(reporterId);
            } else {
                next.delete(reporterId);
            }
            return next;
        });
    }, []);

    const onStay = () => {
        setPendingDiscard(null);
        blocker.reset?.();
    };
    const onLeave = () => {
        if (pendingDiscard) {
            discard(pendingDiscard);
        }
        setPendingDiscard(null);
        blocker.proceed?.();
    };

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return;
        }
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            // Browsers that predate the preventDefault() form of the prompt (Chrome before 119) only show it when
            // returnValue is set.
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasUnsavedChanges]);

    return (
        <UnsavedChangesGuardContext.Provider value={{ confirmDiscard, discard, reportUnsavedChanges }}>
            {children}
            <Modal
                open={blocker.state === "blocked" || pendingDiscard !== null}
                title={t("modals:unsavedChanges.title")}
                onClose={onStay}
                secondaryActionText={t("modals:unsavedChanges.button.keepEditing")}
                endButton={<DangerButton onClick={onLeave}>{t("modals:unsavedChanges.button.discard")}</DangerButton>}
            >
                <DialogContentText>{t("modals:unsavedChanges.description")}</DialogContentText>
            </Modal>
        </UnsavedChangesGuardContext.Provider>
    );
};

export const useConfirmDiscardChanges = () => useContext(UnsavedChangesGuardContext).confirmDiscard;

export const useDiscardChanges = () => useContext(UnsavedChangesGuardContext).discard;

export const useReportUnsavedChanges = (hasUnsavedChanges: boolean) => {
    const reporterId = useId();
    const { reportUnsavedChanges } = useContext(UnsavedChangesGuardContext);

    useEffect(() => {
        reportUnsavedChanges(reporterId, hasUnsavedChanges);
        return () => reportUnsavedChanges(reporterId, false);
    }, [reportUnsavedChanges, reporterId, hasUnsavedChanges]);
};

export const ReportUnsavedChanges = ({ hasUnsavedChanges }: { hasUnsavedChanges: boolean }) => {
    useReportUnsavedChanges(hasUnsavedChanges);
    return null;
};
